<template>
  <q-page class="review-page">
    <div class="review-container">
      <!-- Loading state -->
      <div v-if="loading" class="review-container">
        <q-skeleton type="text" width="200px" dark style="font-size: 1.5rem;" />
        <q-skeleton type="text" width="300px" dark style="margin-top: 4px;" />
        <q-skeleton type="rect" height="160px" dark style="border-radius: 16px; margin-top: 24px;" />
        <q-skeleton v-for="n in 3" :key="n" type="rect" height="200px" dark style="border-radius: 16px; margin-top: 16px;" />
      </div>

      <!-- Error state -->
      <div v-else-if="error" style="text-align: center; padding: 48px 16px;">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p style="color: var(--oa-text-muted); margin-top: 12px;">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchSubmission"
        />
      </div>

      <!-- Loaded -->
      <template v-else-if="submission">
        <!-- Back link -->
        <router-link to="/evaluations" class="back-link">
          <q-icon name="arrow_back" size="14px" />
          Evaluaciones
        </router-link>

        <!-- Header -->
        <h1 class="page-title">Revisar entrega</h1>
        <p class="page-subtitle">{{ submission.template?.name ?? 'Entrega' }}</p>

        <!-- Submission info card -->
        <div class="info-card">
          <div class="info-grid">
            <div class="info-item">
              <p class="info-label">Alumno</p>
              <p class="info-value">
                {{ submission.user?.firstName ?? '?' }} {{ submission.user?.lastName ?? '' }}
              </p>
            </div>
            <div class="info-item">
              <p class="info-label">Estado</p>
              <q-badge :color="statusColor(submission.status)" :label="statusLabel(submission.status)" />
            </div>
            <div class="info-item">
              <p class="info-label">Tipo</p>
              <p class="info-value">{{ typeLabels[submission.submissionType] ?? submission.submissionType }}</p>
            </div>
            <div class="info-item">
              <p class="info-label">Intento</p>
              <p class="info-value">#{{ submission.attemptNumber }} de {{ submission.template?.maxAttempts ?? '?' }}</p>
            </div>
          </div>

          <!-- Content viewer -->
          <div v-if="submission.content" class="content-viewer">
            <p class="content-label">Contenido</p>
            <p class="content-text">{{ submission.content }}</p>
          </div>
          <div v-if="submission.videoUrl" class="content-link-wrap">
            <p class="content-label">Video</p>
            <a :href="submission.videoUrl" target="_blank" rel="noopener noreferrer" class="content-link">
              {{ submission.videoUrl }}
            </a>
          </div>
          <div v-if="submission.fileUrl" class="content-link-wrap">
            <p class="content-label">Documento</p>
            <a :href="submission.fileUrl" target="_blank" rel="noopener noreferrer" class="content-link">
              {{ submission.fileUrl }}
            </a>
          </div>
          <div v-if="submission.notes" class="notes-viewer">
            <p class="content-label">Notas del alumno</p>
            <p class="content-text">{{ submission.notes }}</p>
          </div>
        </div>

        <!-- Previous evaluations -->
        <div v-if="(submission.evaluations ?? []).length > 0" class="prev-evals-card">
          <h3 class="prev-evals-title">Evaluaciones anteriores</h3>
          <div class="prev-evals-list">
            <div
              v-for="ev in submission.evaluations"
              :key="ev.id"
              class="prev-eval-item"
            >
              <div class="prev-eval-header">
                <span class="prev-eval-type">
                  {{ ev.evaluatorType === 'auto' ? 'Automatica' : ev.evaluatorType === 'ai_agent' ? 'Agente IA' : 'Evaluador humano' }}
                </span>
                <q-badge color="amber-8" text-color="black" :label="`${parseFloat(ev.weightedScore).toFixed(1)}/10`" />
              </div>
              <p v-if="ev.feedback" class="prev-eval-feedback">{{ ev.feedback }}</p>
            </div>
          </div>
        </div>

        <!-- Review form -->
        <form @submit.prevent="handleSubmit">
          <!-- Evaluator type -->
          <div class="evaluator-card">
            <label class="field-label">Tipo de evaluacion</label>
            <div class="eval-type-selector">
              <button
                v-for="opt in evaluatorOptions"
                :key="opt.value"
                type="button"
                class="eval-type-btn"
                :class="{ 'eval-type-btn-active': evaluatorType === opt.value }"
                @click="evaluatorType = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Real-time score preview (sticky) -->
          <div class="score-preview-sticky">
            <div class="score-preview-header">
              <span class="score-preview-label">Score parcial en tiempo real</span>
              <span class="score-preview-value">{{ weightedTotal.toFixed(1) }}/10</span>
            </div>
            <div class="score-preview-dims">
              <div
                v-for="dim in dimensions"
                :key="dim.key"
                class="score-dim-preview"
              >
                <span class="score-dim-name">{{ dim.label }}</span>
                <span class="score-dim-value">{{ dimPreviews[dim.key]?.normalized.toFixed(1) ?? '0.0' }}</span>
              </div>
            </div>
            <div class="score-cnap-preview">
              <span
                class="cnap-badge"
                :style="{ background: cnapDisplay.bg, color: cnapDisplay.color }"
              >
                {{ cnapDisplay.label }}
              </span>
            </div>
          </div>

          <!-- Score grid by dimension -->
          <div
            v-for="dim in dimensions"
            :key="dim.key"
            class="dimension-card"
          >
            <!-- Dimension header -->
            <div class="dim-header">
              <div>
                <h3 class="dim-title">{{ dim.label }}</h3>
                <span class="dim-weight-label">Peso: {{ dim.weight }}%</span>
              </div>
              <div class="dim-avg">
                <p class="dim-avg-label">Promedio</p>
                <p class="dim-avg-value">{{ dimPreviews[dim.key]?.normalized.toFixed(1) ?? '0.0' }}/10</p>
              </div>
            </div>

            <!-- Indicators -->
            <div class="dim-indicators">
              <div
                v-for="ind in dim.indicators"
                :key="ind.key"
                class="indicator-row"
              >
                <!-- Indicator info -->
                <div class="ind-info">
                  <div class="ind-header">
                    <span class="ind-label">{{ ind.label }}</span>
                    <span
                      v-if="ind.description && ind.description.includes('ELIMINATORIO')"
                      class="ind-elim-badge"
                    >
                      ELIMINATORIO
                    </span>
                  </div>
                  <p v-if="ind.description" class="ind-description">{{ ind.description }}</p>
                </div>

                <!-- Current score display -->
                <div class="ind-score-display">
                  <span class="ind-current-score">{{ scores[dim.key]?.[ind.key] ?? 3 }}/5</span>
                  <span
                    class="ind-score-label"
                    :style="{ color: getScaleColor(scores[dim.key]?.[ind.key] ?? 3) }"
                  >
                    {{ getScaleLabel(scores[dim.key]?.[ind.key] ?? 3) }}
                  </span>
                </div>

                <!-- Score buttons -->
                <div class="score-buttons">
                  <button
                    v-for="val in [1, 2, 3, 4, 5]"
                    :key="val"
                    type="button"
                    class="score-btn"
                    :style="{
                      background: scores[dim.key]?.[ind.key] === val ? getScaleColor(val) : 'var(--oa-bg-3)',
                      color: scores[dim.key]?.[ind.key] === val
                        ? (val <= 2 ? 'var(--color-marble-cream)' : 'var(--color-marble-cream)')
                        : 'var(--oa-text-disabled)',
                    }"
                    @click="setScore(dim.key, ind.key, val)"
                  >
                    {{ val }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Final score summary -->
          <div
            class="final-score-card"
            :style="{ borderColor: cnapDisplay.color }"
          >
            <div class="final-score-header">
              <h3 class="final-score-title">Nota final ponderada</h3>
              <p class="final-score-value">{{ weightedTotal.toFixed(2) }}/10</p>
            </div>
            <div class="final-score-cnap">
              <span
                class="cnap-badge-large"
                :style="{ color: cnapDisplay.color }"
              >
                {{ cnapDisplay.label }}
              </span>
            </div>
          </div>

          <!-- Feedback -->
          <div class="feedback-card">
            <div class="form-field">
              <label class="field-label">Devolucion general</label>
              <q-input
                v-model="feedback"
                type="textarea"
                :rows="4"
                dark
                outlined
                placeholder="Devolucion constructiva para el alumno..."
              />
            </div>

            <div class="form-field">
              <label class="field-label">Fortalezas (una por linea)</label>
              <q-input
                v-model="strengthsText"
                type="textarea"
                :rows="3"
                dark
                outlined
                placeholder="Buena demostracion tecnica&#10;Manejo seguro del espacio&#10;Comunicacion clara"
              />
            </div>

            <div class="form-field">
              <label class="field-label">Areas de mejora (una por linea)</label>
              <q-input
                v-model="improvementsText"
                type="textarea"
                :rows="3"
                dark
                outlined
                placeholder="Usar nombres de los alumnos&#10;Ofrecer mas regresiones&#10;Mejorar gestion del tiempo"
              />
            </div>
          </div>

          <!-- Submit error -->
          <q-banner v-if="submitError" rounded class="submit-error-banner">
            <template #avatar>
              <q-icon name="error" color="red" />
            </template>
            {{ submitError }}
          </q-banner>

          <!-- Actions -->
          <div class="form-actions">
            <q-btn
              type="submit"
              no-caps
              unelevated
              :loading="submitting"
              :disable="submitting"
              label="Guardar evaluacion"
              class="submit-btn"
            />
            <q-btn
              no-caps
              flat
              label="Cancelar"
              to="/evaluations"
              style="color: var(--oa-text-muted);"
            />
          </div>
        </form>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { api } from 'boot/axios';

// --- Types ---
interface RubricIndicator {
  key: string;
  label: string;
  description: string;
  maxScore: number;
}

interface RubricDimension {
  key: string;
  label: string;
  weight: number;
  indicators: RubricIndicator[];
}

interface EvalTemplate {
  name: string;
  description: string | null;
  rubricConfig: { dimensions: RubricDimension[] };
  maxAttempts: number;
  passingScore: number;
}

interface Evaluation {
  id: string;
  evaluatorType: string;
  weightedScore: string;
  evaluatedAt: string;
  feedback: string | null;
}

interface SubmissionData {
  id: string;
  submissionType: string;
  status: string;
  attemptNumber: number;
  submittedAt: string;
  content: string | null;
  videoUrl: string | null;
  fileUrl: string | null;
  notes: string | null;
  template?: EvalTemplate | null;
  user?: { firstName: string; lastName: string } | null;
  evaluations?: Evaluation[];
}

// --- Scale constants (matching v1 eval-constants) ---
const SCALE_LABELS: Record<number, string> = {
  1: 'Deficiente',
  2: 'Necesita mejora',
  3: 'Aceptable',
  4: 'Bueno',
  5: 'Excelente',
};

const SCALE_COLORS: Record<number, string> = {
  1: '#ef4444',
  2: '#f97316',
  3: '#eab308',
  4: 'var(--oa-success)',
  5: 'var(--color-aged-gold)',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  in_review: 'En revision',
  reviewed: 'Revisada',
  revision_requested: 'Correccion',
  approved: 'Aprobada',
  rejected: 'Rechazada',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'amber',
  in_review: 'blue',
  reviewed: 'teal',
  revision_requested: 'orange',
  approved: 'green',
  rejected: 'red',
};

const typeLabels: Record<string, string> = {
  video: 'Video',
  document: 'Documento',
  link: 'Enlace',
  text: 'Texto',
  practice_log: 'Registro de practica',
};

const evaluatorOptions = [
  { value: 'human' as const, label: 'Humano (15%)' },
  { value: 'ai_agent' as const, label: 'Agente IA (40%)' },
  { value: 'auto' as const, label: 'Automatica (45%)' },
];

// --- Hooks ---
const route = useRoute();
const router = useRouter();
const $q = useQuasar();

// --- State ---
const loading = ref(true);
const error = ref<string | null>(null);
const submission = ref<SubmissionData | null>(null);
const submitting = ref(false);
const submitError = ref<string | null>(null);

const evaluatorType = ref<'auto' | 'ai_agent' | 'human'>('human');
const scores = reactive<Record<string, Record<string, number>>>({});
const feedback = ref('');
const strengthsText = ref('');
const improvementsText = ref('');

// --- Computed ---
const dimensions = computed<RubricDimension[]>(() =>
  submission.value?.template?.rubricConfig?.dimensions ?? [],
);

const dimPreviews = computed(() => {
  const result: Record<string, { avg: number; normalized: number }> = {};
  for (const dim of dimensions.value) {
    const dimScores = scores[dim.key] ?? {};
    const vals = Object.values(dimScores);
    const avg = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    // Normalize from 1-5 to 1-10: (avg - 1) * 2.25 + 1
    const normalized = vals.length > 0 ? (avg - 1) * 2.25 + 1 : 0;
    result[dim.key] = { avg, normalized };
  }
  return result;
});

const weightedTotal = computed(() => {
  let total = 0;
  for (const dim of dimensions.value) {
    const preview = dimPreviews.value[dim.key];
    if (preview && preview.normalized > 0) {
      total += (preview.normalized * dim.weight) / 100;
    }
  }
  return total;
});

const cnapDisplay = computed(() => {
  const score = weightedTotal.value;
  if (score >= 9) return { label: 'Positivo (P)', color: 'var(--oa-tier-cc)', bg: 'color-mix(in srgb, var(--oa-tier-cc) 15%, transparent)' };
  if (score >= 7) return { label: 'Adecuado (A)', color: 'var(--oa-gold-full)', bg: 'color-mix(in srgb, var(--oa-gold-full) 15%, transparent)' };
  if (score >= 5) return { label: 'Necesita Mejora (N)', color: '#fb923c', bg: 'color-mix(in srgb, #fb923c 15%, transparent)' };
  return { label: 'Critico (C)', color: '#ef4444', bg: 'color-mix(in srgb, #ef4444 15%, transparent)' };
});

// --- Methods ---
function setScore(dimKey: string, indKey: string, value: number) {
  if (!scores[dimKey]) {
    scores[dimKey] = {};
  }
  scores[dimKey][indKey] = value;
}

function getScaleLabel(val: number): string {
  return SCALE_LABELS[val] ?? '';
}

function getScaleColor(val: number): string {
  return SCALE_COLORS[val] ?? 'var(--oa-text-muted)';
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function statusColor(status: string): string {
  return STATUS_COLORS[status] ?? 'grey';
}

function initScores() {
  for (const dim of dimensions.value) {
    if (!scores[dim.key]) {
      scores[dim.key] = {};
    }
    for (const ind of dim.indicators) {
      if (scores[dim.key][ind.key] === undefined) {
        scores[dim.key][ind.key] = 3; // Default mid-score
      }
    }
  }
}

// --- Data fetch ---
async function fetchSubmission() {
  loading.value = true;
  error.value = null;

  try {
    const submissionId = route.params.submissionId as string;
    const { data } = await api.get<SubmissionData>(`/evaluations/submissions/${submissionId}`);
    submission.value = data;
    initScores();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error al cargar la entrega';
    error.value = message;
  } finally {
    loading.value = false;
  }
}

// --- Submit ---
async function handleSubmit() {
  if (!submission.value) return;

  submitting.value = true;
  submitError.value = null;

  try {
    const rawScores: Record<string, Record<string, number>> = {};
    for (const dim of dimensions.value) {
      rawScores[dim.key] = {};
      for (const ind of dim.indicators) {
        rawScores[dim.key][ind.key] = scores[dim.key]?.[ind.key] ?? 3;
      }
    }

    const body = {
      evaluatorType: evaluatorType.value,
      rawScores,
      feedback: feedback.value || undefined,
      strengths: strengthsText.value
        ? strengthsText.value.split('\n').filter(Boolean)
        : undefined,
      improvements: improvementsText.value
        ? improvementsText.value.split('\n').filter(Boolean)
        : undefined,
    };

    await api.post(`/evaluations/submissions/${submission.value.id}/review`, body);

    $q.notify({
      type: 'positive',
      message: 'Evaluacion guardada',
      caption: 'La evaluacion fue registrada correctamente.',
      position: 'top',
      timeout: 3000,
    });

    router.push('/evaluations');
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const resp = (err as { response?: { data?: { error?: string } } }).response;
      submitError.value = resp?.data?.error ?? 'Error al guardar la evaluacion';
    } else {
      submitError.value = err instanceof Error ? err.message : 'Error desconocido';
    }
  } finally {
    submitting.value = false;
  }
}

onMounted(fetchSubmission);
</script>

<style lang="scss" scoped>
.review-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.review-container {
  max-width: 900px;
  width: 100%;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--oa-gold-full);
  text-decoration: none;
  margin-bottom: 16px;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.page-subtitle {
  color: var(--oa-text-muted);
  font-size: 14px;
  margin: 4px 0 0;
}

// --- Info card ---
.info-card {
  margin-top: 24px;
  padding: 20px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 640px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin: 0;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.content-viewer {
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: var(--oa-bg-3);
}

.content-label {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin: 0 0 4px;
}

.content-text {
  font-size: 14px;
  color: var(--oa-text-primary);
  margin: 0;
  white-space: pre-wrap;
}

.content-link-wrap {
  margin-top: 16px;
}

.content-link {
  font-size: 14px;
  color: var(--oa-gold-full);
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
}

.notes-viewer {
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--oa-gold-full) 5%, var(--oa-bg-3));
}

// --- Previous evaluations ---
.prev-evals-card {
  margin-top: 16px;
  padding: 20px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
}

.prev-evals-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 12px;
}

.prev-evals-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.prev-eval-item {
  padding: 12px;
  border-radius: 8px;
  background: var(--oa-bg-3);
}

.prev-eval-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.prev-eval-type {
  font-size: 14px;
  color: var(--oa-text-primary);
}

.prev-eval-feedback {
  font-size: 13px;
  color: var(--oa-text-secondary);
  margin: 8px 0 0;
}

// --- Evaluator type ---
.evaluator-card {
  margin-top: 24px;
  padding: 20px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
}

.eval-type-selector {
  display: flex;
  gap: 8px;
}

.eval-type-btn {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid var(--oa-border);
  background: transparent;
  color: var(--oa-text-muted);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--oa-gold-dim);
    color: var(--oa-text-primary);
  }
}

.eval-type-btn-active {
  border-color: var(--oa-gold-full);
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
  font-weight: 500;
}

// --- Score preview (sticky) ---
.score-preview-sticky {
  position: sticky;
  top: 16px;
  z-index: 10;
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--oa-bg-2) 95%, transparent);
  backdrop-filter: blur(8px);
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 20%, var(--oa-border));
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.score-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.score-preview-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
}

.score-preview-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--oa-gold-full);
}

.score-preview-dims {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.score-dim-preview {
  flex: 1;
  text-align: center;
}

.score-dim-name {
  display: block;
  font-size: 10px;
  color: var(--oa-text-disabled);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.score-dim-value {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--oa-text-primary);
}

.score-cnap-preview {
  margin-top: 8px;
  text-align: center;
}

.cnap-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

// --- Dimension card ---
.dimension-card {
  margin-top: 16px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  overflow: hidden;
}

.dim-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--oa-border);
}

.dim-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.dim-weight-label {
  font-size: 12px;
  color: var(--oa-text-muted);
}

.dim-avg {
  text-align: right;
}

.dim-avg-label {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin: 0;
}

.dim-avg-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 0;
}

// --- Indicators ---
.dim-indicators {
  padding: 0;
}

.indicator-row {
  padding: 16px 20px;
  border-bottom: 1px solid var(--oa-border);

  &:last-child {
    border-bottom: none;
  }
}

.ind-info {
  margin-bottom: 8px;
}

.ind-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ind-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
}

.ind-elim-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: color-mix(in srgb, #ef4444 15%, transparent);
  color: #ef4444;
}

.ind-description {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
  line-height: 1.4;
}

.ind-score-display {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.ind-current-score {
  font-size: 14px;
  font-weight: 700;
  color: var(--oa-gold-full);
}

.ind-score-label {
  font-size: 11px;
  font-weight: 500;
}

.score-buttons {
  display: flex;
  gap: 6px;
}

.score-btn {
  flex: 1;
  padding: 10px 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    transform: scale(1.05);
  }
}

// --- Final score ---
.final-score-card {
  margin-top: 16px;
  padding: 20px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 2px solid var(--oa-border);
}

.final-score-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.final-score-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.final-score-value {
  font-size: 30px;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 0;
}

.final-score-cnap {
  margin-top: 8px;
}

.cnap-badge-large {
  font-size: 14px;
  font-weight: 600;
}

// --- Feedback card ---
.feedback-card {
  margin-top: 16px;
  padding: 20px;
  border-radius: 16px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
}

.form-field {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin-bottom: 8px;
}

// --- Submit error ---
.submit-error-banner {
  background: color-mix(in srgb, var(--oa-danger) 10%, var(--oa-bg-2));
  color: var(--oa-danger);
  margin-top: 16px;
}

// --- Actions ---
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-bottom: 48px;
}

.submit-btn {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
  border-radius: 12px;
  padding: 10px 24px;
}
</style>
